import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getMyAssignments, type MyAssignment } from '../api/assignment';
import useThrowAsyncError from '../hooks/useThrowAsyncError';
import IdentityContext from './IdentityContext';

type MyAssignmentsContextValue = {
  assignments: MyAssignment[];
  refreshAssignments: () => Promise<void>;
};

const defaultMyAssignmentsContextValue: MyAssignmentsContextValue = {
  assignments: [],
  refreshAssignments: () => Promise.resolve(),
};

const MyAssignmentsContext = createContext<MyAssignmentsContextValue>(defaultMyAssignmentsContextValue);

export default MyAssignmentsContext;

export const MyAssignmentsContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [assignments, setAssignments] = useState<MyAssignment[]>([]);

  const {
    identity: { isAuthenticated },
  } = useContext(IdentityContext);

  const handleError = useThrowAsyncError();

  const fetchAssignments = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const fetchedAssignments = await getMyAssignments();

        setAssignments(fetchedAssignments);
      } catch (err) {
        handleError(err);
      }
    }
  }, [isAuthenticated, handleError]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const myAssignmentsContextValue = useMemo(
    () => ({
      assignments,
      refreshAssignments: fetchAssignments,
    }),
    [assignments, fetchAssignments],
  );

  return <MyAssignmentsContext.Provider value={myAssignmentsContextValue}>{children}</MyAssignmentsContext.Provider>;
};
